import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  Checkbox,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Badge,
  Switch,
  Spinner,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateBotResponse from "./CreateBotResponse";
import LocaleAnswerTabs from "./LocaleAnswerTabs";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { useLocales, translationsArrayToMap, validateNameTranslations } from "utils/translations";
import TranslationsCell from "components/localeNameFields/TranslationsCell";

const BOT_RESPONSES_PAGE_ID = PAGE_IDS.BOT_RESPONSES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyAnswerTranslation = () => ({ answer: "" });

const ListBotResponse = () => {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "lg" });

  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editableResponse, setEditableResponse] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === BOT_RESPONSES_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      // ignore malformed permissions blob
    }
  }, []);

  const fetchResponses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/bot/response?page_id=${BOT_RESPONSES_PAGE_ID}`);
      setResponses(response?.data || []);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch responses."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const handleCreated = () => {
    setShowCreateModal(false);
    fetchResponses();
  };

  const handleEdit = (item) => {
    setEditErrors({});
    setEditableResponse({
      ...item,
      translations: translationsArrayToMap(item.translations, emptyAnswerTranslation, locales),
      platform: item.platform || "",
      order: item.order ?? "",
    });
  };

  const setEditTranslationField = (code, value) => {
    setEditableResponse((prev) => ({
      ...prev,
      translations: { ...prev.translations, [code]: { ...emptyAnswerTranslation(), ...prev.translations[code], answer: value } },
    }));
  };

  const handleSave = async () => {
    const errors = {};
    if (!editableResponse.trigger.trim()) errors.trigger = t("Trigger is required");
    Object.assign(errors, validateNameTranslations(editableResponse.translations, locales, t, "answer", t("Answer is required")));
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      const translationsPayload = locales
        .map(({ code }) => ({ locale: code, answer: (editableResponse.translations[code]?.answer || "").trim() }))
        .filter((tr) => tr.answer);

      const data = {
        page_id: BOT_RESPONSES_PAGE_ID,
        responseId: editableResponse.id,
        type: editableResponse.type,
        trigger: editableResponse.trigger.trim(),
        matchType: editableResponse.type === "FAQ" ? editableResponse.matchType : undefined,
        platform: editableResponse.platform || null,
        isActive: editableResponse.isActive,
        ...(editableResponse.order !== "" && { order: Number(editableResponse.order) }),
        translations: translationsPayload,
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/response`,
        "PUT",
        data
      );
      if (!response) {
        throw new Error("Failed to update response");
      }
      await fetchResponses();
      setEditableResponse(null);
      toast({
        title: t("Response updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update response."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (responseId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/response`,
        "DELETE",
        { page_id: BOT_RESPONSES_PAGE_ID, responseId }
      );
      if (!response) {
        throw new Error("Failed to delete response");
      }
      setResponses((prev) => prev.filter((r) => r.id !== responseId));
      toast({
        title: t("Response deleted successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to delete response."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Quick one-click on/off toggle right in the table, same pattern used by
  // Languages/SubCategory/Bot Channels — separate from the full edit modal.
  const handleToggleActive = async (item) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/bot/response`,
        "PUT",
        { page_id: BOT_RESPONSES_PAGE_ID, responseId: item.id, isActive: !item.isActive }
      );
      if (!response) {
        throw new Error("Failed to update response");
      }
      setResponses((prev) => prev.map((r) => (r.id === item.id ? { ...r, isActive: !r.isActive } : r)));
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to toggle response status."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const filteredResponses = responses.filter((r) => (r.trigger || "").toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Bot Responses")}
          </Text>
          <Button
            onClick={() => setShowCreateModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
          >
            {t("Add Response")}
          </Button>
        </Flex>
        <Input
          placeholder={t("Search by trigger")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
          w={{ base: "100%", md: "220px" }}
          borderColor={GOLD_BORDER}
          focusBorderColor={GOLD_BORDER}
        />
      </Flex>
      <Text fontSize="xs" color="gray.500" px={{ base: "10px", md: "25px" }} mb="16px">
        {t("These answer regular users messaging the bot privately. Reserved admin commands (/start, /status, /restart_wa, /qr) are separate and can't be created here.")}
      </Text>

      <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
        {loading ? (
          <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Trigger")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Type")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Match")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Platform")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Answer")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredResponses.map((item) => (
                <Tr key={item.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td fontFamily={item.type === "COMMAND" ? "mono" : undefined}>{item.trigger}</Td>
                  <Td>
                    <Badge colorScheme={item.type === "COMMAND" ? "purple" : "blue"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                      {t(item.type)}
                    </Badge>
                  </Td>
                  <Td>{item.type === "FAQ" ? t(item.matchType) : "—"}</Td>
                  <Td>{item.platform ? t(item.platform) : t("Both")}</Td>
                  <Td maxW="220px">
                    <TranslationsCell translations={item.translations} locales={locales} field="answer" noOfLines={1} />
                  </Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Badge colorScheme={item.isActive ? "green" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                        {item.isActive ? t("Active") : t("Inactive")}
                      </Badge>
                      <Switch
                        size="sm"
                        isChecked={item.isActive}
                        isDisabled={!permissions.canEdit}
                        onChange={() => handleToggleActive(item)}
                        colorScheme="brand"
                      />
                    </Flex>
                  </Td>
                  <Td>
                    <Flex>
                      <Button
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canEdit}
                        onClick={() => handleEdit(item)}
                        leftIcon={<MdEdit />}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        isDisabled={!permissions.canDelete}
                        onClick={() => handleDelete(item.id)}
                        leftIcon={<MdDelete />}
                      >
                        {t("Delete")}
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {editableResponse && (
        <Modal isOpen={true} onClose={() => setEditableResponse(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                {t("Edit Response")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Type")}</Text>
                  <Select value={editableResponse.type} onChange={(e) => setEditableResponse({ ...editableResponse, type: e.target.value })} {...fieldProps}>
                    <option value="FAQ">{t("FAQ (free-text Q&A)")}</option>
                    <option value="COMMAND">{t("Command (e.g. /status)")}</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Platform")}</Text>
                  <Select value={editableResponse.platform} onChange={(e) => setEditableResponse({ ...editableResponse, platform: e.target.value })} {...fieldProps}>
                    <option value="">{t("Both")}</option>
                    <option value="TELEGRAM">{t("Telegram")}</option>
                    <option value="WHATSAPP">{t("WhatsApp")}</option>
                  </Select>
                </Box>
              </SimpleGrid>

              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("Trigger")}</Text>
                <Input
                  value={editableResponse.trigger}
                  onChange={(e) => setEditableResponse({ ...editableResponse, trigger: e.target.value })}
                  {...fieldProps}
                  isInvalid={!!editErrors.trigger}
                />
                {editErrors.trigger && <Text color="red.500" fontSize="xs" mt="4px">{editErrors.trigger}</Text>}
              </Box>

              {editableResponse.type === "FAQ" && (
                <Box mt="16px">
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Match Type")}</Text>
                  <Select value={editableResponse.matchType} onChange={(e) => setEditableResponse({ ...editableResponse, matchType: e.target.value })} {...fieldProps}>
                    <option value="CONTAINS">{t("Contains (matches anywhere in the message)")}</option>
                    <option value="EXACT">{t("Exact (message must match exactly)")}</option>
                  </Select>
                </Box>
              )}

              <LocaleAnswerTabs
                locales={locales}
                translations={editableResponse.translations}
                errors={editErrors}
                onChange={setEditTranslationField}
                borderColor={GOLD_BORDER}
              />

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
                <Checkbox
                  isChecked={editableResponse.isActive}
                  onChange={(e) => setEditableResponse({ ...editableResponse, isActive: e.target.checked })}
                  colorScheme="brand"
                >
                  {t("Active")}
                </Checkbox>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Order")}</Text>
                  <Input
                    type="number"
                    value={editableResponse.order}
                    onChange={(e) => setEditableResponse({ ...editableResponse, order: e.target.value })}
                    {...fieldProps}
                  />
                </Box>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={() => setEditableResponse(null)}>
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Create Response")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateBotResponse onCreated={handleCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ListBotResponse;
